import React, { useState, useEffect, useRef } from 'react';
import './VoiceReader.css';

const SPEEDS = [0.78, 0.88, 1, 1.12, 1.25];
const DEFAULT_SPEED_INDEX = 1;

function htmlToText(html) {
  const div = document.createElement('div');
  div.innerHTML = html || '';
  return (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
}

function makeWords(text) {
  const words = [];
  const re = /\S+/g;
  let match;
  while ((match = re.exec(text)) !== null) {
    words.push({ text: match[0], start: match.index, end: match.index + match[0].length });
  }
  return words;
}

function buildChunks(text, maxLen = 165) {
  if (!text) return [];
  // Safari-safe split (no lookbehind)
  const parts = text.replace(/([.!?;:])\s+/g, '$1\x00').split('\x00').filter(Boolean);
  if (!parts.length) return [{ text, words: makeWords(text) }];
  const chunks = [];
  let buf = '';
  for (const p of parts) {
    if (buf && buf.length + p.length + 1 > maxLen) {
      chunks.push({ text: buf, words: makeWords(buf) });
      buf = p;
    } else {
      buf = buf ? `${buf} ${p}` : p;
    }
  }
  if (buf) chunks.push({ text: buf, words: makeWords(buf) });
  return chunks.length ? chunks : [{ text, words: makeWords(text) }];
}

function pickVoice(voices) {
  if (!voices || !voices.length) return null;
  const preferred = [
    /Google UK English Female/i,
    /Microsoft Sonia/i,
    /Microsoft Libby/i,
    /Samantha/i,
    /Serena/i,
    /Daniel/i,
    /Moira/i,
    /Karen/i,
    /Fiona/i,
    /Google US English/i,
  ];
  for (const pattern of preferred) {
    const found = voices.find(v => pattern.test(v.name));
    if (found) return found;
  }
  const uk = voices.find(v => /en[-_]GB/i.test(v.lang) && !/compact/i.test(v.name));
  if (uk) return uk;
  return voices.find(v => /^en/i.test(v.lang) && !/compact/i.test(v.name)) || null;
}

function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60).toString().padStart(2, '0');
  return `${mins}:${secs}`;
}

function wordIndexFromChar(chunk, charIndex) {
  if (!chunk?.words?.length) return 0;
  const idx = chunk.words.findIndex(w => charIndex >= w.start && charIndex <= w.end);
  if (idx >= 0) return idx;
  for (let i = chunk.words.length - 1; i >= 0; i--) {
    if (charIndex >= chunk.words[i].start) return i;
  }
  return 0;
}

// Wrap every word in the article DOM with a <span class="vr-word-token">
function wrapDomWords(el) {
  const spans = [];
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  let node;
  while ((node = walker.nextNode())) textNodes.push(node);

  for (const tn of textNodes) {
    const text = tn.nodeValue || '';
    if (!text.trim()) continue;
    const frag = document.createDocumentFragment();
    let last = 0;
    const re = /\S+/g;
    let m;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)));
      const span = document.createElement('span');
      span.className = 'vr-word-token';
      span.textContent = m[0];
      spans.push(span);
      frag.appendChild(span);
      last = m.index + m[0].length;
    }
    if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
    tn.parentNode.replaceChild(frag, tn);
  }
  return spans;
}

// Remove word spans without breaking the text
function unwrapDomWords(el) {
  el.querySelectorAll('.vr-word-token').forEach(span => {
    if (span.parentNode) {
      span.parentNode.replaceChild(document.createTextNode(span.textContent), span);
    }
  });
  try { el.normalize(); } catch (_) {}
}

// audioUrl + audioWords (both set once Pooja's real voice has been generated
// for this post in admin) switch the whole player onto a real <audio>
// element instead of the browser's synthetic voice — same controls, same
// word-by-word highlight, driven by ElevenLabs' character-timing alignment
// instead of speechSynthesis' onboundary events. Falls back to the original
// browser-voice reader untouched when neither is present.
export default function VoiceReader({ title, content, contentRef, audioUrl, audioWords }) {
  const hasRealAudio = !!(audioUrl && Array.isArray(audioWords) && audioWords.length);

  const [synthSupported, setSynthSupported] = useState(false);
  const [status, setStatus] = useState('idle');
  const [speedIdx, setSpeedIdx] = useState(DEFAULT_SPEED_INDEX);
  const [chunkIdx, setChunkIdx] = useState(0);
  const [spokenWords, setSpokenWords] = useState(0);
  const [voiceName, setVoiceName] = useState(hasRealAudio ? 'Pooja' : '');
  const [audioTime, setAudioTime] = useState(0);

  const chunksRef = useRef([]);
  const voicesRef = useRef([]);
  const wordOffsetsRef = useRef([]);
  const wordSpansRef = useRef([]);
  const activeSpanRef = useRef(null);
  const genRef = useRef(0);
  const statusRef = useRef('idle');
  const speedRef = useRef(SPEEDS[DEFAULT_SPEED_INDEX]);
  const audioRef = useRef(null);
  const wordCursorRef = useRef(0);

  useEffect(() => {
    setSynthSupported('speechSynthesis' in window);
  }, []);

  const supported = hasRealAudio || synthSupported;

  useEffect(() => {
    if (hasRealAudio || !synthSupported) return;
    const loadVoices = () => {
      voicesRef.current = window.speechSynthesis.getVoices();
      const v = pickVoice(voicesRef.current);
      setVoiceName(v?.name || '');
    };
    loadVoices();
    window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
    return () => window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
  }, [synthSupported, hasRealAudio]);

  function setHighlight(globalIdx) {
    const span = wordSpansRef.current[globalIdx];
    if (span === activeSpanRef.current) return;
    if (activeSpanRef.current) activeSpanRef.current.classList.remove('vr-word--active');
    activeSpanRef.current = span || null;
    if (span) {
      span.classList.add('vr-word--active');
      const rect = span.getBoundingClientRect();
      if (rect.bottom > window.innerHeight - 80) {
        span.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  function clearHighlight() {
    if (activeSpanRef.current) {
      activeSpanRef.current.classList.remove('vr-word--active');
      activeSpanRef.current = null;
    }
  }

  // Wrap the article DOM into per-word spans for highlighting — needed by
  // both playback modes whenever the article body is on the page.
  useEffect(() => {
    if (!supported) return;
    genRef.current++;
    if (!hasRealAudio) window.speechSynthesis?.cancel();

    if (activeSpanRef.current) {
      activeSpanRef.current.classList.remove('vr-word--active');
      activeSpanRef.current = null;
    }

    const el = contentRef?.current;
    let ttsText = '';

    if (el) {
      unwrapDomWords(el);
      const spans = wrapDomWords(el);
      wordSpansRef.current = spans;
      ttsText = spans.map(s => s.textContent).join(' ');
    } else {
      wordSpansRef.current = [];
      ttsText = title ? `${title}. ${htmlToText(content)}` : htmlToText(content);
    }

    if (!hasRealAudio) {
      chunksRef.current = buildChunks(ttsText);
      let offset = 0;
      wordOffsetsRef.current = chunksRef.current.map(chunk => {
        const start = offset;
        offset += chunk.words.length;
        return start;
      });
    }

    wordCursorRef.current = 0;
    setStatus('idle');
    setChunkIdx(0);
    setSpokenWords(0);
    setAudioTime(0);
    statusRef.current = 'idle';

    const genRefAtEffect = genRef;
    const contentEl = el;
    return () => {
      genRefAtEffect.current++;
      if (!hasRealAudio) window.speechSynthesis?.cancel();
      if (activeSpanRef.current) {
        activeSpanRef.current.classList.remove('vr-word--active');
        activeSpanRef.current = null;
      }
      if (contentEl) unwrapDomWords(contentEl);
      wordSpansRef.current = [];
    };
  }, [content, title, supported, contentRef, hasRealAudio]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!supported || hasRealAudio) return;
    const gen = genRef;
    return () => { gen.current++; window.speechSynthesis?.cancel(); };
  }, [supported, hasRealAudio]);

  // ── Browser-voice (speechSynthesis) playback ──────────────────────────
  function speakFrom(idx, speed, gen) {
    if (gen !== genRef.current) return;
    const chunks = chunksRef.current;
    if (!chunks.length || idx >= chunks.length) {
      genRef.current++;
      clearHighlight();
      setStatus('idle');
      setChunkIdx(0);
      setSpokenWords(chunks.reduce((s, c) => s + c.words.length, 0));
      statusRef.current = 'idle';
      return;
    }

    setChunkIdx(idx);
    setSpokenWords(wordOffsetsRef.current[idx] || 0);

    const utter = new SpeechSynthesisUtterance(chunks[idx].text);
    utter.rate = speed;
    utter.pitch = 0.98;
    utter.volume = 1;
    utter.lang = 'en-GB';
    const voice = pickVoice(voicesRef.current);
    if (voice) { utter.voice = voice; setVoiceName(voice.name); }

    utter.onboundary = event => {
      if (gen !== genRef.current) return;
      const wordIdx = wordIndexFromChar(chunks[idx], event.charIndex || 0);
      setSpokenWords((wordOffsetsRef.current[idx] || 0) + wordIdx + 1);
      setHighlight((wordOffsetsRef.current[idx] || 0) + wordIdx);
    };
    utter.onend = () => {
      setSpokenWords((wordOffsetsRef.current[idx] || 0) + chunks[idx].words.length);
      speakFrom(idx + 1, speed, gen);
    };
    utter.onerror = e => {
      if (e.error === 'interrupted' || e.error === 'canceled') return;
      if (gen !== genRef.current) return;
      clearHighlight();
      setStatus('idle');
      setChunkIdx(0);
      statusRef.current = 'idle';
    };

    window.speechSynthesis.speak(utter);
  }

  // ── Real-audio playback (Pooja's ElevenLabs voice) ─────────────────────
  const onAudioTimeUpdate = () => {
    const el = audioRef.current;
    if (!el) return;
    const t = el.currentTime;
    setAudioTime(t);
    let cursor = wordCursorRef.current;
    while (cursor < audioWords.length - 1 && audioWords[cursor].end < t) cursor++;
    while (cursor > 0 && audioWords[cursor].start > t) cursor--;
    wordCursorRef.current = cursor;
    setSpokenWords(cursor + 1);
    setHighlight(cursor);
  };

  const onAudioEnded = () => {
    clearHighlight();
    setStatus('idle');
    setSpokenWords(audioWords?.length || 0);
    statusRef.current = 'idle';
  };

  const play = () => {
    if (!supported) return;
    if (hasRealAudio) {
      audioRef.current?.play().catch(() => {});
      setStatus('playing');
      statusRef.current = 'playing';
      return;
    }
    if (status === 'paused') {
      window.speechSynthesis.resume();
      setStatus('playing');
      statusRef.current = 'playing';
      return;
    }
    genRef.current++;
    const gen = genRef.current;
    window.speechSynthesis.cancel();
    clearHighlight();
    statusRef.current = 'playing';
    setStatus('playing');
    const startIdx = status === 'idle' ? chunkIdx : 0;
    setTimeout(() => {
      if (gen !== genRef.current) return;
      speakFrom(startIdx, speedRef.current, gen);
    }, 50);
  };

  const pause = () => {
    if (hasRealAudio) {
      audioRef.current?.pause();
      setStatus('paused');
      statusRef.current = 'paused';
      return;
    }
    window.speechSynthesis.pause();
    setStatus('paused');
    statusRef.current = 'paused';
  };

  const stop = () => {
    if (hasRealAudio) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      wordCursorRef.current = 0;
      clearHighlight();
      setStatus('idle');
      setSpokenWords(0);
      setAudioTime(0);
      statusRef.current = 'idle';
      return;
    }
    genRef.current++;
    window.speechSynthesis.cancel();
    clearHighlight();
    setStatus('idle');
    setChunkIdx(0);
    setSpokenWords(0);
    statusRef.current = 'idle';
  };

  const cycleSpeed = () => {
    const next = (speedIdx + 1) % SPEEDS.length;
    setSpeedIdx(next);
    speedRef.current = SPEEDS[next];
    if (hasRealAudio) {
      if (audioRef.current) audioRef.current.playbackRate = SPEEDS[next];
      return;
    }
    if (status === 'playing') {
      genRef.current++;
      const gen = genRef.current;
      const idx = chunkIdx;
      window.speechSynthesis.cancel();
      clearHighlight();
      setTimeout(() => {
        if (gen !== genRef.current) return;
        speakFrom(idx, SPEEDS[next], gen);
      }, 50);
    }
  };

  if (!supported) return null;

  const chunks = chunksRef.current;
  const totalWords = hasRealAudio ? audioWords.length : chunks.reduce((sum, c) => sum + c.words.length, 0);
  const currentWords = status === 'idle' ? 0 : Math.min(totalWords, Math.max(0, spokenWords));
  const pct = totalWords > 0 ? Math.round((currentWords / totalWords) * 100) : 0;
  const estimatedSeconds = hasRealAudio
    ? (audioWords[audioWords.length - 1]?.end || 0)
    : (totalWords ? (totalWords / (150 * SPEEDS[speedIdx])) * 60 : 0);
  const elapsedSeconds = hasRealAudio ? audioTime : (totalWords ? (estimatedSeconds * currentWords) / totalWords : 0);

  return (
    <div className="vr-wrap" role="region" aria-label="Article audio player">
      {hasRealAudio && (
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="none"
          onTimeUpdate={onAudioTimeUpdate}
          onEnded={onAudioEnded}
        />
      )}
      <div className="vr-inner">
        <span className="vr-icon-mic" aria-hidden="true">🎧</span>
        <div className="vr-title-block">
          <span className="vr-label">Listen gently</span>
          <span className="vr-time">
            {formatTime(elapsedSeconds)} / {formatTime(estimatedSeconds)}
            {voiceName && <span className="vr-voice"> · {hasRealAudio ? "In Pooja's voice" : voiceName}</span>}
          </span>
        </div>

        <div className="vr-controls">
          {status === 'playing' ? (
            <button className="vr-btn vr-btn--pause" onClick={pause} aria-label="Pause">
              <PauseIcon />
            </button>
          ) : (
            <button className="vr-btn vr-btn--play" onClick={play} aria-label={status === 'paused' ? 'Resume' : 'Play'}>
              <PlayIcon />
            </button>
          )}
          {status !== 'idle' && (
            <button className="vr-btn vr-btn--stop" onClick={stop} aria-label="Stop">
              <StopIcon />
            </button>
          )}
          <button className="vr-btn vr-btn--speed" onClick={cycleSpeed} aria-label={`Speed: ${SPEEDS[speedIdx]}×`}>
            {SPEEDS[speedIdx]}×
          </button>
        </div>

        {status !== 'idle' && (
          <div className="vr-progress-wrap">
            <div className="vr-progress" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
              <div className="vr-progress-fill" style={{ width: `${pct}%` }} />
            </div>
            <span className="vr-pct">{pct}%</span>
          </div>
        )}
      </div>
    </div>
  );
}

const PlayIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
    <polygon points="2,1 13,7 2,13" />
  </svg>
);
const PauseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
    <rect x="2" y="1" width="4" height="12" rx="1" />
    <rect x="8" y="1" width="4" height="12" rx="1" />
  </svg>
);
const StopIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
    <rect x="1" y="1" width="10" height="10" rx="1" />
  </svg>
);
